import React, { useEffect, useState } from "react";
import { toast } from "@jammable/ui-core";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { useAuth } from "../context/AuthProvider";
import { useRouter } from "next/router";
import { loadAuth, loadAuthApi } from "../utils/firebaseAuth";
import {
  customGoogleErrorMessages,
  customSignInErrorMessages,
  customSignUpMessages,
} from "../utils/tools";
import Link from "next/link";
import { Spinner } from "@jammable/ui-components/Loader";
import { NextSeo } from "next-seo";
import { useT } from "@jammable/ui-core/src/i18n";
import {
  AuthError,
  AuthLayout,
  AuthTabs,
  GoogleButton,
  OrDivider,
  authErrorText,
  authFieldError,
  authInput,
  authInputError,
  authLink,
  authPrimary,
} from "../components/auth/JamAuth";

const LoginPage = ({ clientIp }) => {
  const [error, setError] = useState(null);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user, setUser, updateUser } = useAuth();
  const t = useT();

  // The Firebase SDK loads on demand (utils/firebaseAuth); start now so it
  // is there by the time a button is pressed.
  useEffect(() => {
    loadAuth().catch(() => {});
  }, []);

  const router = useRouter();
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .trim()
      .email(t("auth.invalidEmail"))
      .required(t("auth.emailRequired")),
    password: yup
      .string()
      .min(6, t("auth.passwordMin"))
      .required(t("auth.passwordRequired")),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      // Handle your login logic here
      setLoading(true);
      loadAuthApi()
        .then(({ auth, fb }) =>
          fb.signInWithEmailAndPassword(
            auth,
            values.email.trim(),
            values.password,
          ),
        )
        .then(() => {
          router.push("/");
          toast.success(t("auth.welcomeBack"));
        })
        .catch((error) => {
          setError(
            authErrorText(t, "signIn", customSignInErrorMessages, error),
          );
        })
        .finally(() => {
          setLoading(false);
        });
      // Redirect or update state as needed
    },
  });

  const handleGoogleSignIn = async () => {
    try {
      const { auth, fb } = await loadAuthApi();
      const result = await fb.signInWithPopup(
        auth,
        new fb.GoogleAuthProvider(),
      );

      const details = fb.getAdditionalUserInfo(result);
      if (!details) {
        await result.user.delete();
        toast.error(t("auth.registerError"));
        throw new Error("Additional user information not available");
      }

      if (details?.isNewUser) {
        axios
          .post("/api/auth/register", {
            ip: clientIp,
            user: { ...result?.user, username: result?.user?.displayName },
          })
          .then(async (res) => {
            updateUser({
              avatar: res?.data?.user?.avatar,
              username: res?.data?.user?.username,
              subscriptions: [],
              credits: 0,
            });
            try {
              if (window.tolt_referral) {
                window.tolt.signup(res?.data?.user?.email);
              }
            } catch (error) {}
            toast.success(t("auth.welcomeNew"));
            router.push("/");
          })
          .catch(async (err) => {
            if (!err?.response?.data?.isDeleted) {
              await result.user.delete();
            }
            if (err.response.data.message) {
              setError(err.response.data.message);
            } else {
              toast.error(t("auth.registerError"));
            }
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        toast.success(t("auth.welcomeBack"));
        router.push("/");
      }
    } catch (error) {
      setError(authErrorText(t, "google", customGoogleErrorMessages, error));
      setLoading(false);
    }
  };

  return (
    <>
      <NextSeo
        title={t("auth.seo.loginTitle")}
        description={t("auth.seo.loginDescription")}
        canonical="https://www.jammable.com/login"
        openGraph={{
          title: "Login - Jammable",
          description: "Login to your Jammable account.",
          url: "https://www.jammable.com/login",
        }}
      />
      <AuthLayout
        heading={t("auth.login.heading")}
        blurb={t("auth.login.blurb")}
      >
        <AuthTabs active="login" />
        <GoogleButton
          onClick={() => {
            handleGoogleSignIn();
          }}
        >
          {t("auth.continueGoogle")}
        </GoogleButton>
        <OrDivider />
        <form
          onChange={() => {
            setError(null);
          }}
          onSubmit={formik.handleSubmit}
          className="tw-flex tw-w-full tw-flex-col tw-gap-3.5"
        >
          {error && <AuthError>{error}</AuthError>}

          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <input
              className={`${authInput} ${formik.touched.email && formik.errors.email ? authInputError : ""}`}
              placeholder={t("auth.email")}
              aria-label={t("auth.email")}
              inputMode="email"
              autoComplete="email"
              name="email"
              onChange={formik.handleChange}
              value={formik.values.email}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email && (
              <div className={`${authFieldError} tw-px-5`}>
                {formik.errors.email}
              </div>
            )}
          </div>
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <input
              className={`${authInput} ${formik.touched.password && formik.errors.password ? authInputError : ""}`}
              placeholder={t("auth.password")}
              aria-label={t("auth.password")}
              type="password"
              autoComplete="current-password"
              name="password"
              onChange={formik.handleChange}
              value={formik.values.password}
              onBlur={formik.handleBlur}
            />
            {formik.touched.password && formik.errors.password && (
              <div className={`${authFieldError} tw-px-5`}>
                {formik.errors.password}
              </div>
            )}
          </div>
          <Link
            href={`/forgotpassword`}
            className={`${authLink} tw-self-end tw-text-[13px]`}
          >
            {t("auth.forgotPassword")}
          </Link>
          <button type="submit" className={authPrimary} disabled={loading}>
            {loading ? (
              <Spinner className="tw-border-white tw-border-3" size={4} />
            ) : (
              t("auth.logIn")
            )}
          </button>
        </form>
        <div className="tw-text-center tw-text-[13px] tw-font-bold tw-text-jam-muted">
          {t("auth.login.noAccount")}{" "}
          <Link className={authLink} href={`/signup`}>
            {t("auth.signUp")}
          </Link>
        </div>
      </AuthLayout>
    </>
  );
};

export default LoginPage;

export async function getServerSideProps(context) {
  const { req } = context;

  const getClientIp = (req) => {
    const ip =
      (req.headers["x-forwarded-for"] || "").split(",").pop().trim() ||
      req.connection.remoteAddress ||
      req.socket.remoteAddress ||
      req.connection.socket.remoteAddress;
    return ip;
  };

  try {
    const clientIp = getClientIp(req);
    return {
      props: {
        clientIp,
      },
    };
  } catch (error) {
    return {
      props: {
        a: "a",
      },
    };
  }
}
